import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getTasks, createTask, updateTask, deleteTask, getProjects, takeTask, submitTask, approveTask, rejectTask } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { StatusBadge } from '../components/app';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  ListChecks,
  Clock,
  CurrencyDollar,
  Check,
  X,
  Play,
  Hand,
  Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const SKILLS = ['frontend', 'backend', 'mobile', 'qa', 'design', 'devops', 'pm', 'soporte'];

export function TasksPage() {
  const { t } = useLanguage();
  const { user, isManager } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_id: '',
    task_type: 'desarrollo',
    priority: 'media',
    complexity: 'media',
    story_points: 1,
    estimated_hours: 0,
    deadline: '',
    required_skills: [],
    status: 'pendiente',
    price: 0,
    is_marketplace: false,
    checklist: [],
    acceptance_criteria: '',
  });
  const [newChecklistItem, setNewChecklistItem] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      const [tasksRes, projectsRes] = await Promise.all([
        getTasks(params),
        getProjects().catch(() => ({ data: [] })),
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [priorityFilter, statusFilter, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await updateTask(editingTask._id, formData);
      } else {
        await createTask(formData);
      }
      toast.success(t('successSaved'));
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || '',
      description: task.description || '',
      project_id: task.project_id || '',
      task_type: task.task_type || 'desarrollo',
      priority: task.priority || 'media',
      complexity: task.complexity || 'media',
      story_points: task.story_points || 1,
      estimated_hours: task.estimated_hours || 0,
      deadline: task.deadline || '',
      required_skills: task.required_skills || [],
      status: task.status || 'pendiente',
      price: task.price || 0,
      is_marketplace: task.is_marketplace || false,
      checklist: task.checklist || [],
      acceptance_criteria: task.acceptance_criteria || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await deleteTask(id);
        toast.success(t('successDeleted'));
        fetchData();
      } catch (error) {
        toast.error(t('errorOccurred'));
      }
    }
  };

  const handleTakeTask = async (taskId) => {
    try {
      await takeTask(taskId);
      toast.success('Task taken successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleSubmitTask = async (taskId) => {
    try {
      await submitTask(taskId);
      toast.success('Task submitted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleApproveTask = async (taskId) => {
    try {
      await approveTask(taskId);
      toast.success(t('successApproved'));
      fetchData();
      setViewingTask(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleRejectTask = async (taskId) => {
    const reason = window.prompt('Reason for rejection:');
    if (reason !== null) {
      try {
        await rejectTask(taskId, reason);
        toast.success(t('successRejected'));
        fetchData();
        setViewingTask(null);
      } catch (error) {
        toast.error(error.response?.data?.detail || t('errorOccurred'));
      }
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      project_id: '',
      task_type: 'desarrollo',
      priority: 'media',
      complexity: 'media',
      story_points: 1,
      estimated_hours: 0,
      deadline: '',
      required_skills: [],
      status: 'pendiente',
      price: 0,
      is_marketplace: false,
      checklist: [],
      acceptance_criteria: '',
    });
  };

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      required_skills: prev.required_skills.includes(skill)
        ? prev.required_skills.filter(s => s !== skill)
        : [...prev.required_skills, skill]
    }));
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setFormData(prev => ({
        ...prev,
        checklist: [...prev.checklist, newChecklistItem.trim()]
      }));
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (index) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Si es empleado, solo ver sus tareas asignadas
    if (user?.role === 'empleado') {
      const matchesAssigned = task.assigned_to === user?._id;
      return matchesSearch && matchesAssigned;
    }
    
    return matchesSearch;
  });

  const getProjectName = (projectId) => {
    const project = projects.find(p => p._id === projectId);
    return project?.name || 'No project';
  };

  return (
    <Layout title={t('tasks')}>
      <div className="space-y-6" data-testid="tasks-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 valhalla-input"
                data-testid="task-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 valhalla-input">
                <SelectValue placeholder={t('status')} />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="pendiente">{t('pending')}</SelectItem>
                <SelectItem value="disponible">{t('available')}</SelectItem>
                <SelectItem value="tomada">{t('taken')}</SelectItem>
                <SelectItem value="en_progreso">{t('inProgress')}</SelectItem>
                <SelectItem value="entregada">{t('delivered')}</SelectItem>
                <SelectItem value="en_revision">{t('inReview')}</SelectItem>
                <SelectItem value="aprobada">{t('approved')}</SelectItem>
                <SelectItem value="rechazada">{t('rejected')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-32 valhalla-input">
                <SelectValue placeholder={t('priority')} />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="baja">{t('low')}</SelectItem>
                <SelectItem value="media">{t('medium')}</SelectItem>
                <SelectItem value="alta">{t('high')}</SelectItem>
                <SelectItem value="critica">{t('critical')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isManager && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold" data-testid="new-task-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('newTask')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#151B26] border-[#242E42] max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-[#F3F4F6] font-['Outfit']">
                    {editingTask ? t('edit') : t('newTask')}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('taskTitle')}</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="valhalla-input"
                      required
                      data-testid="task-title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('description')}</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="valhalla-input min-h-[80px]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">Project</Label>
                      <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({...formData, project_id: v === "none" ? "" : v})}>
                        <SelectTrigger className="valhalla-input">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#151B26] border-[#242E42]">
                          <SelectItem value="none">No project</SelectItem>
                          {projects.map(proj => (
                            <SelectItem key={proj._id} value={proj._id}>{proj.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">{t('taskType')}</Label>
                      <Select value={formData.task_type} onValueChange={(v) => setFormData({...formData, task_type: v})}>
                        <SelectTrigger className="valhalla-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#151B26] border-[#242E42]">
                          <SelectItem value="desarrollo">{t('coding')}</SelectItem>
                          <SelectItem value="correccion">{t('bugfix')}</SelectItem>
                          <SelectItem value="qa">{t('qa')}</SelectItem>
                          <SelectItem value="diseno">{t('design')}</SelectItem>
                          <SelectItem value="analisis">{t('analysis')}</SelectItem>
                          <SelectItem value="soporte">{t('support')}</SelectItem>
                          <SelectItem value="documentacion">{t('documentation')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">{t('priority')}</Label>
                      <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                        <SelectTrigger className="valhalla-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#151B26] border-[#242E42]">
                          <SelectItem value="baja">{t('low')}</SelectItem>
                          <SelectItem value="media">{t('medium')}</SelectItem>
                          <SelectItem value="alta">{t('high')}</SelectItem>
                          <SelectItem value="critica">{t('critical')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">{t('complexity')}</Label>
                      <Select value={formData.complexity} onValueChange={(v) => setFormData({...formData, complexity: v})}>
                        <SelectTrigger className="valhalla-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#151B26] border-[#242E42]">
                          <SelectItem value="baja">{t('lowComplexity')}</SelectItem>
                          <SelectItem value="media">{t('mediumComplexity')}</SelectItem>
                          <SelectItem value="alta">{t('highComplexity')}</SelectItem>
                          <SelectItem value="experta">{t('expert')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">{t('storyPoints')}</Label>
                      <Input
                        type="number"
                        value={formData.story_points}
                        onChange={(e) => setFormData({...formData, story_points: parseInt(e.target.value) || 1})}
                        className="valhalla-input"
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">{t('estimatedHours')}</Label>
                      <Input
                        type="number"
                        value={formData.estimated_hours}
                        onChange={(e) => setFormData({...formData, estimated_hours: parseFloat(e.target.value) || 0})}
                        className="valhalla-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">{t('deadline')}</Label>
                      <Input
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                        className="valhalla-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#9CA3AF]">{t('price')} ($)</Label>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                        className="valhalla-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('requiredSkills')}</Label>
                    <div className="flex flex-wrap gap-2">
                      {SKILLS.map(skill => (
                        <Badge
                          key={skill}
                          variant="outline"
                          className={`cursor-pointer transition-all ${
                            formData.required_skills.includes(skill)
                              ? 'bg-[#E2B05E]/20 border-[#E2B05E] text-[#E2B05E]'
                              : 'border-[#242E42] text-[#9CA3AF] hover:border-[#3AB8D6]'
                          }`}
                          onClick={() => toggleSkill(skill)}
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="marketplace"
                      checked={formData.is_marketplace}
                      onCheckedChange={(checked) => setFormData({...formData, is_marketplace: checked, status: checked ? 'disponible' : 'pendiente'})}
                    />
                    <Label htmlFor="marketplace" className="text-[#9CA3AF] cursor-pointer">
                      {t('isMarketplace')} - Publish to marketplace
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('checklist')}</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                        className="valhalla-input"
                        placeholder="Add checklist item"
                      />
                      <Button type="button" onClick={addChecklistItem} variant="outline" className="border-[#242E42]">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-1 mt-2">
                      {formData.checklist.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 rounded bg-[#0B0E14]/50">
                          <span className="flex-1 text-sm text-[#F3F4F6]">{item}</span>
                          <button type="button" onClick={() => removeChecklistItem(index)} className="text-[#9CA3AF] hover:text-red-400">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('acceptanceCriteria')}</Label>
                    <Textarea
                      value={formData.acceptance_criteria}
                      onChange={(e) => setFormData({...formData, acceptance_criteria: e.target.value})}
                      className="valhalla-input min-h-[60px]"
                      placeholder="Define acceptance criteria..."
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-[#242E42]">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#242E42] text-[#9CA3AF]">
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14]" data-testid="save-task-btn">
                      {t('save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Tasks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTasks.map((task) => (
            <div key={task._id} className="valhalla-card p-5" data-testid={`task-card-${task._id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-[#F3F4F6] mb-1">{task.title}</h3>
                  <p className="text-xs text-[#9CA3AF]">{getProjectName(task.project_id)}</p>
                </div>
                {task.is_marketplace && (
                  <Badge className="bg-[#E2B05E]/20 text-[#E2B05E] border-[#E2B05E]/30 text-xs">
                    Marketplace
                  </Badge>
                )}
              </div>

              <p className="text-sm text-[#9CA3AF] line-clamp-2 mb-3">{task.description || 'No description'}</p>

              <div className="flex items-center gap-2 flex-wrap mb-3">
                <StatusBadge value={task.status} />
                <StatusBadge value={task.priority} />
                <StatusBadge value={task.complexity} />
              </div>

              <div className="flex items-center gap-4 text-sm text-[#9CA3AF] mb-4">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{task.estimated_hours || 0}h</span>
                </div>
                <div className="flex items-center gap-1">
                  <CurrencyDollar className="w-4 h-4" />
                  <span>${task.price || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <ListChecks className="w-4 h-4" />
                  <span>{task.story_points || 0} pts</span>
                </div>
              </div>

              {task.required_skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {task.required_skills.slice(0, 3).map(skill => (
                    <Badge key={skill} variant="outline" className="text-xs border-[#242E42] text-[#9CA3AF]">
                      {skill}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-[#242E42]">
                <div className="flex gap-1">
                  {task.status === 'disponible' && (
                    <Button size="sm" onClick={() => handleTakeTask(task._id)} className="bg-[#3AB8D6] hover:bg-[#3AB8D6]/80 text-white text-xs">
                      <Hand className="w-3 h-3 mr-1" />
                      {t('takeTask')}
                    </Button>
                  )}
                  {(task.status === 'tomada' || task.status === 'en_progreso') && task.assigned_to === user?._id && (
                    <Button size="sm" onClick={() => handleSubmitTask(task._id)} className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] text-xs">
                      <Play className="w-3 h-3 mr-1" />
                      {t('submitTask')}
                    </Button>
                  )}
                  {(task.status === 'entregada' || task.status === 'en_revision') && isManager && (
                    <>
                      <Button size="sm" onClick={() => handleApproveTask(task._id)} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs">
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button size="sm" onClick={() => handleRejectTask(task._id)} className="bg-red-500 hover:bg-red-600 text-white text-xs">
                        <X className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setViewingTask(task)} className="text-[#9CA3AF] hover:text-[#3AB8D6]">
                    <Eye className="w-4 h-4" />
                  </Button>
                  {isManager && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(task)} className="text-[#9CA3AF] hover:text-[#3AB8D6]">
                        <PencilSimple className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(task._id)} className="text-[#9CA3AF] hover:text-red-400">
                        <Trash className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTasks.length === 0 && !loading && (
          <div className="valhalla-card p-12 text-center">
            <ListChecks className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">{t('noData')}</p>
          </div>
        )}

        {/* Task Detail Dialog */}
        <Dialog open={!!viewingTask} onOpenChange={() => setViewingTask(null)}>
          <DialogContent className="bg-[#151B26] border-[#242E42] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-[#F3F4F6] font-['Outfit']">
                {viewingTask?.title}
              </DialogTitle>
            </DialogHeader>
            {viewingTask && (
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  <StatusBadge value={viewingTask.status} />
                  <StatusBadge value={viewingTask.priority} />
                  <StatusBadge value={viewingTask.complexity} />
                  {viewingTask.is_marketplace && (
                    <Badge className="bg-[#E2B05E]/20 text-[#E2B05E]">Marketplace</Badge>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-medium text-[#9CA3AF] mb-1">{t('description')}</h4>
                  <p className="text-[#F3F4F6]">{viewingTask.description || 'No description'}</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-[#9CA3AF] mb-1">{t('estimatedHours')}</h4>
                    <p className="text-[#F3F4F6]">{viewingTask.estimated_hours || 0}h</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#9CA3AF] mb-1">{t('price')}</h4>
                    <p className="text-[#E2B05E] font-bold">${viewingTask.price || 0}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#9CA3AF] mb-1">{t('storyPoints')}</h4>
                    <p className="text-[#F3F4F6]">{viewingTask.story_points || 0}</p>
                  </div>
                </div>

                {viewingTask.checklist?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-[#9CA3AF] mb-2">{t('checklist')}</h4>
                    <div className="space-y-1">
                      {viewingTask.checklist.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded bg-[#0B0E14]/50">
                          <Checkbox />
                          <span className="text-sm text-[#F3F4F6]">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {viewingTask.acceptance_criteria && (
                  <div>
                    <h4 className="text-sm font-medium text-[#9CA3AF] mb-1">{t('acceptanceCriteria')}</h4>
                    <p className="text-[#F3F4F6] text-sm">{viewingTask.acceptance_criteria}</p>
                  </div>
                )}

                {(viewingTask.status === 'entregada' || viewingTask.status === 'en_revision') && isManager && (
                  <div className="flex gap-3 pt-4 border-t border-[#242E42]">
                    <Button onClick={() => handleApproveTask(viewingTask._id)} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
                      <Check className="w-4 h-4 mr-2" />
                      {t('approveTask')}
                    </Button>
                    <Button onClick={() => handleRejectTask(viewingTask._id)} variant="outline" className="flex-1 border-red-500 text-red-400 hover:bg-red-500/10">
                      <X className="w-4 h-4 mr-2" />
                      {t('rejectTask')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default TasksPage;
